import type { FC, KeyboardEvent } from 'react';
import {
  getAccessibleTabIndex,
  getAriaButtonRoleAttribute,
  handleAccessibleButtonKeyDown,
} from '../Button/ariaHelpers';
import { Icon } from '../Icon/Icon';
import { useConditionalClickHandler } from '../hooks/useConditionalClickHandler';
import { fromRenderAsToElement } from '../utils/InteractiveAtomProps';
import {
  getLinkClassNames,
  getLinkInnerClassNames,
  linkSpinnerWrapperClassNames,
} from './Link.styles';
import {
  type ExternalLinkProps,
  type LinkProps,
  type LinkRenderedAsButtonProps,
  type PageLinkProps,
  fromLinkToIconSize,
} from './Link.types';

export const Link: FC<LinkProps> = ({
  renderAs = 'link',
  accent = 'positive',
  size = 'default',
  className,
  onClick,
  children,
  loading,
  icon,
  iconStyle = 'regular',
  iconPosition = 'end',
  disabled,
  elementProps,
  forwardRef,
  ...restProps
}) => {
  const DynamicComponent: any = fromRenderAsToElement[renderAs];

  let linkIcon = <></>;

  if (icon) {
    linkIcon = (
      <Icon
        aria-hidden={children ? true : undefined}
        glyph={icon}
        size={fromLinkToIconSize[size]}
        iconStyle={iconStyle}
        autoWidth
      />
    );
  }

  const linkDisabled = disabled || loading;

  const handleClick = useConditionalClickHandler<
    HTMLButtonElement | HTMLAnchorElement
  >(linkDisabled, onClick);

  const buttonType =
    renderAs === 'button'
      ? (restProps as LinkRenderedAsButtonProps).type || 'button'
      : undefined;

  return (
    <DynamicComponent
      {...elementProps}
      {...restProps}
      type={buttonType}
      ref={forwardRef}
      disabled={linkDisabled}
      className={getLinkClassNames(className, accent, size, linkDisabled)}
      onClick={handleClick}
      {...getAriaButtonRoleAttribute(renderAs !== 'button')}
      tabIndex={getAccessibleTabIndex(linkDisabled)}
      onKeyDown={(
        e: KeyboardEvent<HTMLButtonElement> | KeyboardEvent<HTMLAnchorElement>,
      ) => {
        if (renderAs !== 'button') {
          handleAccessibleButtonKeyDown(e, linkDisabled);
        }
      }}
    >
      <span className={getLinkInnerClassNames(loading)}>
        {iconPosition === 'start' && linkIcon}
        {children}
        {iconPosition === 'end' && linkIcon}
      </span>
      {loading && (
        <span className={linkSpinnerWrapperClassNames}>
          <Icon
            aria-hidden
            glyph='fa-spinner'
            loading
            size={fromLinkToIconSize[size]}
          />
        </span>
      )}
    </DynamicComponent>
  );
};

// ExternalLink component is just a shortcut for Link with pre-defined parameters.
// It is designed to render as anchor HTML element with specific icon opening external page in new browser tab.
export const ExternalLink: FC<ExternalLinkProps> = ({
  elementProps,
  ...restProps
}) => {
  return (
    <Link
      renderAs='anchor'
      icon='fa-arrow-up-right-from-square'
      iconStyle='regular'
      iconPosition='end'
      elementProps={{ ...elementProps, target: '_blank', rel: 'noreferrer' }}
      {...restProps}
    />
  );
};

// PageLink component is just a short-hand to Link with pre-defined parameters.
// Is is designed to render as router Link element with specific icon navigation user to internal routed page.
export const PageLink: FC<PageLinkProps> = (props) => {
  return (
    <Link
      renderAs='link'
      icon='fa-link-simple'
      iconStyle='regular'
      iconPosition='end'
      {...props}
    />
  );
};
